//library
import React, { useCallback, useState, useMemo, useEffect, useRef } from "react";

//dto
import ArticleCategoryTableProps, { ArticleCategoryDataDto, ArticleCategoryUpdateDto } from "dto/components/pages/article-category/page-article-category-dto";

import { statusOptions } from "constants/status-options";

//common
import formatIndexNumber from "common/format-index-number";

//components
import AppTextarea from "components/app-textarea";
import AppPagination from "components/app-pagination";
import AppSelectInput from "components/app-select-input";

//assets
// import arrowUp from "assets/images/components/app-faq-table/arrow-up.svg";
import chevronUp from "assets/images/components/app-faq-table/chevron-up.svg";
// import arrowDown from "assets/images/components/app-faq-table/arrow-down.svg";
import editIcon from "assets/images/components/app-articles-table/edit-icon.svg";
// import wasteBinIcon from "assets/images/components/app-link-input/trash-icon.svg";
import chevronDown from "assets/images/components/app-faq-table/chevron-down.svg";

const ArticleCategoryTable: React.FC<ArticleCategoryTableProps> = ({ data, setDisableAddNew, newRow, setNewRow, onUpdateArticleCategory, onCreateArticleCategory, onHandlePageChange, fetching, paginationTable }) => {
	const newRowRef = useRef<HTMLTableRowElement | null>(null);
	const [expandedRows, setExpandedRows] = useState<string[]>([]);
	const [editingRow, setEditingRow] = useState<string | null>();
	const [isReadyData, setIsReadyData] = useState<boolean>(fetching);
	const [categories, setCategories] = useState<ArticleCategoryDataDto>(data);
	const [errors, setErrors] = useState<Record<string, Record<string, string>>>({});
	const startIndex = paginationTable.currentPage * paginationTable.tableSize;
	const [updatedData, setUpdatedData] = useState<ArticleCategoryUpdateDto>();
	const isEmptyData = useMemo(() => categories.length === 0 && !fetching, [categories.length, fetching]);

	const toggleRow = useCallback((id: string) => {
		setExpandedRows((prev) => (prev.includes(id) ? prev.filter((rowId) => rowId !== id) : [...prev, id]));
	}, []);

	//prettier-ignore
	const onHandleEdit = useCallback((id: string) => {
        setEditingRow(id);
        setDisableAddNew(true);
    }, [setDisableAddNew]);

	//prettier-ignore
	const onHandleInputChange = useCallback((id: string, field: "title" | "status" , value: string) => {
		const updatedCategory = updatedData ?? categories.find((cat) => cat.id === id);

		if (updatedCategory) {
			const updatedData = { ...updatedCategory, [field]: value};

			setUpdatedData(updatedData);

			setErrors((prev) => ({ ...prev, [id]: { ...prev[id], [field]: value.trim() ? "" : "This field is required" }}));
		}
    }, [categories, updatedData]);

	//prettier-ignore
	const onHandleNewInputChange = useCallback((field: string, value: string) => {
		setNewRow((prev: any) => ({ ...prev, [field]: value }));

		setErrors((prev) => ({ ...prev, new: { ...prev["new"], [field]: value.trim() ? "" : "This field is required" }}));
	}, [setNewRow]);

	const validateRow = (rowData: any) => {
		const requiredFields = ["title", "status"];
		const newErrors: Record<string, string> = {};

		requiredFields.forEach((field) => {
			if (!rowData[field]?.trim()) newErrors[field] = "This field is required";
		});
		return newErrors;
	};

	//prettier-ignore
	const onHandleSave = useCallback((id: string) => {
		if (updatedData) {
			const rowErrors = validateRow(updatedData);

			if (Object.keys(rowErrors).length > 0) {
				setErrors((prev) => ({ ...prev, [id]: rowErrors }));
				return; 
			}

			const newCategory = categories.map((cat) => (cat.id === id ? { ...cat, ...updatedData } : cat));

			setCategories(newCategory);
			onUpdateArticleCategory(updatedData);
			setIsReadyData(false);
			setEditingRow(null);
			setUpdatedData(null);
			setDisableAddNew(false);
			setErrors((prev) => ({ ...prev, [id]: {} })); 
		}
	}, [categories, updatedData, onUpdateArticleCategory, setDisableAddNew]);

	//prettier-ignore
	// const onHandleMove = useCallback((id: string, move: number) => {
	// 	const index = categories.findIndex((partner) => partner.id === id);
	// 	const updatedCategory = categories[index];

	// 	if ((index > 0  && move === -1) || (index < categories.length - 1 && move === 1)) {
	// 		const newCategory = [...categories];
	// 		const [movedCareer] = newCategory.splice(index, 1);
	// 		const movedDataSortNumber = categories[index + move].sortNumber;
	// 		const updatedData = { ...updatedCategory, sortNumber: movedDataSortNumber };

	// 		if (updatedData) setUpdatedData(updatedData);

	// 		newCategory.splice(index + move, 0, movedCareer);

	// 	 setCategories(newCategory);
	// 	}
	// }, [categories]);

	const truncateText = useCallback((text: string, maxLength: number) => {
		return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
	}, []);

	const onHandleCancel = () => {
		setNewRow(null);
		setEditingRow(null);
		setDisableAddNew(false);
		setErrors({});
	};

	const onHandleSaveNew = () => {
		if (!newRow) return;
		const rowErrors = validateRow(newRow);

		if (Object.keys(rowErrors).length > 0) {
			setErrors((prev) => ({ ...prev, new: rowErrors }));
			return;
		}

		const newCategory = {
			title: newRow?.title || "",
			status: "active",
		};

		onCreateArticleCategory(newCategory);
		setNewRow(null);
		setDisableAddNew(false);
		setErrors((prev) => ({ ...prev, new: {} }));
	};

	const getLabel = (options: { label: string; value: string }[], value: string) => {
		return options.find((option) => option.value === value)?.label || value;
	};

	useEffect(() => {
		if (!fetching) setIsReadyData(true);
	}, [fetching]);

	useEffect(() => {
		if (!editingRow && isReadyData) setCategories(data);
	}, [categories, editingRow, isReadyData, data]);

	useEffect(() => {
		if (newRow && newRowRef.current) newRowRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
	}, [newRow]);

	return (
		<div className="app-article-category-table">
			<div className="category-table">
				<div className="category-table__wrapper">
					<table className="category-table__container">
						<thead className="category-table__header">
							<tr>
								<th className="category-table__th">No.</th>
								<th className="category-table__th">Category Title</th>
								<th className="category-table__th">Status</th>
							</tr>
						</thead>

						<tbody className="category-table__content">
							{categories.map((cat, index) => {
								const isEditing = editingRow === cat.id;
								const isExpanded = expandedRows.includes(cat.id);
								const rowErrors = errors[cat.id] || {};

								return (
									<tr className="category-table__tr" key={cat.id}>
										<td className="category-table__td">{formatIndexNumber(index + 1 + startIndex)}</td>

										<td className="category-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppTextarea fieldRequired name="title" error={rowErrors.title} touched={!!updatedData} value={updatedData?.title ?? cat.title} maxCharLength={35} maxRows={20} onChange={(e) => onHandleInputChange(cat.id, "title", e.target.value)} />
											) : isExpanded ? (
												cat.title
											) : (
												truncateText(cat.title, 25)
											)}
										</td>

										<td className="category-table__td">
											{isEditing ? (
												//prettier-ignore
												<AppSelectInput fieldRequired name="status" error={rowErrors.status} touched={!!updatedData} placeholder={cat.status} value={updatedData?.status ?? cat.status} onChange={(e) => onHandleInputChange(cat.id, "status", e.target.value)} options={statusOptions} searchable={false} />
											) : (
												<span className={cat.status === "active" ? "category-table__td--active" : "category-table__td--inactive"}>{getLabel(statusOptions, cat.status)}</span>
											)}
										</td>

										<td className="category-table__td">
											{isEditing ? (
												<div className="category-table__edit">
													<button className="category-table__cancel" onClick={onHandleCancel}>
														Cancel
													</button>

													{/* <button className="categories-table__icon-button--bordered" onClick={() => onHandleDelete(cat.id)}>
														<img src={wasteBinIcon} alt="delete" />
													</button> */}

													{/* <button className="categories-table__icon-button--bordered" onClick={() => onHandleMove(cat.id, -1)}>
														<img src={arrowUp} alt="up" />
													</button>

													<button className="categories-table__icon-button--bordered" onClick={() => onHandleMove(cat.id, 1)}>
														<img src={arrowDown} alt="down" />
													</button> */}

													<button className="category-table__icon-button--save" onClick={() => onHandleSave(cat.id)}>
														Save
													</button>
												</div>
											) : (
												<div className="category-table__actions">
													<button className="category-table__icon-button" onClick={() => toggleRow(cat.id)}>
														<img src={isExpanded ? chevronUp : chevronDown} alt="" />
													</button>

													<button className="category-table__icon-button" onClick={() => onHandleEdit(cat.id)}>
														<img src={editIcon} alt="Edit" />
													</button>
												</div>
											)}
										</td>
									</tr>
								);
							})}

							{newRow && (
								<tr className="category-table__tr" ref={newRowRef}>
									<td className="category-table__td">{formatIndexNumber(paginationTable.totalElements + 1)}</td>

									<td className="category-table__td">
										{/* prettier-ignore */}
										<AppTextarea name="new-title" error={errors["new"]?.title} touched={!!newRow} fieldRequired value={newRow?.title || ""} maxCharLength={30} maxRows={20} placeholder="Enter text here" onChange={(e) => onHandleNewInputChange("title", e.target.value)} />
									</td>

									<td className="category-table__td">
										{/* prettier-ignore */}
										<AppSelectInput fieldRequired name="status" error={errors["new"]?.status} touched={!!newRow} placeholder="Please Select" value={newRow?.status || ""} onChange={(e) => onHandleNewInputChange("status", e.target.value)} options={statusOptions} searchable={false} />
									</td>

									<td className="category-table__td">
										<div className="category-table__actions">
											<button className="category-table__icon-button--cancel" onClick={onHandleCancel}>
												Cancel
											</button>

											<button className="category-table__icon-button--add" onClick={onHandleSaveNew}>
												Add
											</button>
										</div>
									</td>
								</tr>
							)}

							{fetching && (
								<tr className="category-table__tr">
									<td className="category-table__message" colSpan={6}>
										Loading...
									</td>
								</tr>
							)}
							{isEmptyData && (
								<tr className="category-table__tr">
									<td className="category-table__message" colSpan={6}>
										No records found
									</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>

				{!isEmptyData && !fetching && (
					<AppPagination currentPage={paginationTable.currentPage} totalPages={paginationTable.totalPages} onPageChange={onHandlePageChange} numberOfElements={paginationTable.numberOfElements} />
				)}
			</div>
		</div>
	);
};

export default ArticleCategoryTable;
